import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, ExternalLink, Plus } from 'lucide-react';
import { addDays, format } from 'date-fns';
import { api } from './api';
import PageContainer from './kit/PageContainer';
import { useAuth } from './AuthContext';
import { OwnerChip, OwnerFilter, useStaff, ownerParam } from './kit/OwnerChip';
import { GhlBadge } from './ContactTasks';
import { fromApi } from './kit/time';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import WeekGrid, { parseWeek, weekKey, weekRange } from './calendar/WeekGrid';
import { STATUS_OPTIONS, STATUS_STYLE } from './calendar/StatusSelect';
import DetailsDialog from './calendar/DetailsDialog';
import BookDialog, {
  confirmGhlRefused, confirmOutsideAvailability, errorDetail, fetchFreeSlots, isFreeSlot,
} from './calendar/BookDialog';
import { Button } from '../components/ui/button';
import LongTextCell from '@/components/LongTextCell';

// Calendar page (ticket 143): GHL appointments mirrored, one list for the
// whole team. GHL is the master for everything except status — P2 adds
// status write-through (GHL-first: BFY calls GHL, and only on a 2xx does
// the mirror row change). P3 adds a week grid; view and week live in the URL
// (?view=week&week=YYYY-MM-DD) so reload and Back keep them. P4 adds create
// (New appointment, or click an empty slot) and reschedule (drag a block, or
// the popover's Reschedule button) — same GHL-first rule; a time outside the
// calendar's free slots asks first and then sends override. Title, contact etc.
// on an existing appointment still only change in GHL ("Open in GHL").
// Ticket 189: the list and the week popover show an appointment's address and
// notes, and "Edit details" changes title, notes and address (GHL-first: the
// dialog stays open with the reason if GHL refuses).
//
// Ticket 189: reschedule and status change are optimistic — the row changes at
// once (after any confirm), GHL is called, and a refusal puts the row back with
// a toast. Week is the default view; the last view picked is kept in
// localStorage. A failed load shows an error with Retry, and the list loads
// further date ranges on demand past the API's per-request cap.
const TABS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all', label: 'All' },
];

const VIEW_KEY = 'bfy_calendar_view';
const storedView = () => {
  try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'week'; } catch { return 'week'; }
};

// GET /v2/appointments returns at most this many rows per request
// (appointments_router.LIST_LIMIT) and has no offset: a full page may have more behind it.
const PAGE_CAP = 500;

const day = (iso) => fromApi(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const clock = (iso) => fromApi(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

function when(a) {
  if (!a.start_at) return '—';
  return a.end_at ? `${day(a.start_at)} · ${clock(a.start_at)}–${clock(a.end_at)}` : `${day(a.start_at)} · ${clock(a.start_at)}`;
}

// Tab → query. Upcoming reads forward from now; the others newest first.
function tabParams(tab) {
  if (tab === 'upcoming') return { from: new Date().toISOString(), exclude_status: 'cancelled', order: 'asc' };
  if (tab === 'cancelled') return { status: 'cancelled', order: 'desc' };
  return { order: 'desc' };
}

// The row as it will look once GHL accepts a new time: the end moves with the
// start unless the request names one.
function movedRow(a, body) {
  const length = a.end_at ? fromApi(a.end_at) - fromApi(a.start_at) : 0;
  const end = body.end_at || (length > 0 ? new Date(new Date(body.start_at).getTime() + length).toISOString() : a.end_at);
  return { ...a, start_at: body.start_at, end_at: end };
}

export default function AppointmentsPage() {
  const { user } = useAuth();
  const me = user?.user_id;
  const canViewAll = (user?.permissions || []).includes('loans.view_all');
  const staff = useStaff();
  const [search, setSearch] = useSearchParams();
  const urlView = search.get('view');
  const view = urlView === 'week' || urlView === 'list' ? urlView : storedView();
  const week = weekKey(parseWeek(search.get('week')));
  const [tab, setTab] = useState('upcoming');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(null); // message of the last failed load
  const [reload, setReload] = useState(0); // Retry bumps it to refetch
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const page = useRef({ cursor: null }); // start time where the list's last server page ended
  const [savingIds, setSavingIds] = useState(() => new Map()); // id → what the block says while it saves; saves can overlap
  const [dialog, setDialog] = useState(null); // { mode: 'create' | 'reschedule', appointment?, initialStart? }
  const [detailsFor, setDetailsFor] = useState(null); // the appointment whose details are being edited
  // What is on screen *now*: a save that resolves later places its row by this,
  // not by the view/week/tab/owner it started under.
  const live = useRef({});
  live.current = { view, week, tab, ownerFilter, canViewAll, me };

  const startSaving = (id, label = 'Saving…') => setSavingIds((s) => new Map(s).set(id, label));
  const stopSaving = (id) => setSavingIds((s) => {
    const next = new Map(s);
    next.delete(id);
    return next;
  });

  useEffect(() => {
    let alive = true;
    setRows(null);
    setLoadError(null);
    setHasMore(false);
    // LOs get their own rows from the server's read rule; only owner/admin filter.
    // Week view: that week's window, cancelled included (drawn muted).
    const base = view === 'week' ? { ...weekRange(parseWeek(week)), order: 'asc' } : tabParams(tab);
    const params = { ...base, ...(canViewAll ? ownerParam(ownerFilter, me) : {}) };
    api.get('/v2/appointments', { params })
      .then(({ data }) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : [];
        page.current = { cursor: list.length ? list[list.length - 1].start_at : null };
        setRows(list);
        setHasMore(view === 'list' && list.length >= PAGE_CAP);
      })
      .catch((e) => {
        if (!alive) return;
        setRows([]);
        setLoadError(errorMessage(e, 'Try again in a minute.'));
      });
    return () => { alive = false; };
  }, [view, week, tab, ownerFilter, canViewAll, me, reload]);

  // List past the API cap: ask for the next date range, from where the last
  // server page ended. The API reads by start time (both ends are on start_at),
  // so the next request starts at that row's time; rows already shown are skipped.
  const loadMore = async () => {
    const p = page.current;
    if (loadingMore || !p.cursor) return;
    const { tab: tb, ownerFilter: of, canViewAll: all, me: who } = live.current;
    const t = fromApi(p.cursor);
    const edge = tb === 'upcoming' ? { from: t.toISOString() } : { to: new Date(t.getTime() + 1).toISOString() };
    const params = { ...tabParams(tb), ...edge, ...(all ? ownerParam(of, who) : {}) };
    setLoadingMore(true);
    try {
      const { data } = await api.get('/v2/appointments', { params });
      if (page.current !== p) return; // a different tab / owner loaded meanwhile
      const list = Array.isArray(data) ? data : [];
      const shown = new Set((rows || []).map((r) => r.appointment_id));
      const fresh = list.filter((r) => !shown.has(r.appointment_id));
      const dir = tb === 'upcoming' ? 1 : -1;
      setRows((cur) => {
        const have = new Set((cur || []).map((r) => r.appointment_id));
        return [...(cur || []), ...fresh.filter((r) => !have.has(r.appointment_id))]
          .sort((x, y) => dir * (fromApi(x.start_at) - fromApi(y.start_at)));
      });
      if (list.length) p.cursor = list[list.length - 1].start_at;
      setHasMore(fresh.length > 0 && list.length >= PAGE_CAP);
    } catch (e) {
      toast({ title: 'Could not load more', description: errorMessage(e, 'Try again in a minute.') });
    } finally {
      setLoadingMore(false);
    }
  };

  // Does a saved/moved row belong in what's on screen? Then put it in order.
  const placeRow = (row) => setRows((cur) => {
    if (!cur) return cur;
    const { view: v, week: w, tab: tb, ownerFilter: of, canViewAll: all, me: who } = live.current;
    const rest = cur.filter((r) => r.appointment_id !== row.appointment_id);
    const t = fromApi(row.start_at);
    const cancelled = (row.status || '').toLowerCase() === 'cancelled';
    // Same owner rule as the fetch (ownerParam); LOs only ever see their own rows.
    const owner = all ? ownerParam(of, who).owner_id : undefined;
    let shows = !owner || row.owner_id === owner;
    if (v === 'week') {
      const from = parseWeek(w);
      shows = shows && t >= from && t < addDays(from, 7);
    } else if (tb === 'upcoming') {
      shows = shows && !cancelled && t >= new Date();
    } else {
      shows = shows && (tb === 'cancelled' ? cancelled : true);
    }
    if (!shows) return rest;
    const asc = v === 'week' || tb === 'upcoming';
    return [...rest, row].sort((x, y) => (asc ? 1 : -1) * (fromApi(x.start_at) - fromApi(y.start_at)));
  });

  // A new time for an appointment, from a drag or the reschedule dialog. The
  // caller has already asked about availability (`override` is what they chose).
  // The block moves now; GHL is called GHL-first with a "Moving…" toast; success
  // names the new time, a refusal puts it back and toasts the reason (ticket 216).
  // Never throws.
  const rescheduleRow = async (a, body, override) => {
    placeRow(movedRow(a, body));
    const pending = toast({ title: 'Moving…', description: 'Saving the new time to GoHighLevel.', duration: Infinity });
    const notMoved = (description) => toast({ variant: 'destructive', title: 'Not rescheduled', description });
    try {
      const send = (ov) => api.patch(`/v2/appointments/${a.appointment_id}/time`, { ...body, override: ov });
      let res;
      try {
        res = await send(override);
      } catch (e) {
        const { message, code } = errorDetail(e);
        // GHL refused a time the free-slot list offered: let them override.
        if (override || code !== 'ghl_rejected') throw e;
        pending?.dismiss?.();
        if (!(await confirmGhlRefused(message, 'Move anyway'))) {
          placeRow(a);
          notMoved(message);
          return;
        }
        res = await send(true);
      }
      placeRow(res.data);
      toast({ title: `Moved to ${format(fromApi(res.data?.start_at || body.start_at), "EEE, MMM d 'at' h:mm a")}` });
    } catch (e) {
      placeRow(a);
      notMoved(errorMessage(e, 'GoHighLevel did not accept the change.'));
    }
  };

  const rescheduleFromDialog = async (a, body, override) => {
    startSaving(a.appointment_id, 'Moving…');
    try {
      await rescheduleRow(a, body, override);
    } finally {
      stopSaving(a.appointment_id);
    }
  };

  // Drag drop: the block stays where it was dropped ("Moving…") while that day's
  // free slots for the appointment's calendar are checked; booking outside them
  // asks first, and declining puts it back. Then rescheduleRow PATCHes.
  const dragReschedule = async (a, newStart) => {
    const id = a.appointment_id;
    if (savingIds.has(id)) return;
    startSaving(id, 'Moving…');
    const body = { start_at: newStart.toISOString() };
    placeRow(movedRow(a, body));
    try {
      let override = false;
      if (a.calendar_id) {
        let slots = null;
        try { slots = await fetchFreeSlots(a.calendar_id, format(newStart, 'yyyy-MM-dd'), a.owner_id); } catch (e) { slots = null; } // can't pre-check → GHL decides
        if (slots && !isFreeSlot(slots, newStart)) {
          if (!(await confirmOutsideAvailability(a.calendar_name))) { placeRow(a); return; }
          override = true;
        }
      }
      await rescheduleRow(a, body, override);
    } finally {
      stopSaving(id);
    }
  };

  const showView = (v) => {
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* storage blocked: the URL still holds the view */ }
    setSearch(v === 'week' ? { view: 'week', week } : { view: 'list' });
  };
  const showWeek = (w) => setSearch({ view: 'week', week: w });

  const swapRow = (row) => setRows((cur) => (cur ? cur.map((r) => (r.appointment_id === row.appointment_id ? row : r)) : cur));

  // Optimistic: the new status shows at once (after the cancel confirm); GHL is
  // called; a refusal puts the old row back and toasts the reason.
  const changeStatus = async (a, status) => {
    if (status === a.status) return;
    if (status === 'cancelled') {
      const ok = await confirmDialog({
        title: 'Cancel this appointment?',
        description: 'This updates GoHighLevel and the calendar here.',
        confirmText: 'Cancel appointment',
        destructive: true,
      });
      if (!ok) return;
    }
    startSaving(a.appointment_id);
    swapRow({ ...a, status });
    toast({ title: 'Saving status…', description: 'Sending it to GoHighLevel.', duration: Infinity });
    try {
      const { data } = await api.patch(`/v2/appointments/${a.appointment_id}/status`, { status });
      // cur is null while another view/week loads; swapRow leaves it alone.
      swapRow(data);
      toast({ title: status === 'cancelled' ? 'Appointment cancelled' : 'Status updated' });
    } catch (e) {
      swapRow(a);
      toast({ variant: 'destructive', title: 'Status not changed', description: errorMessage(e, 'GoHighLevel did not accept the change.') });
    } finally {
      stopSaving(a.appointment_id);
    }
  };

  return (
    <PageContainer
      title="Calendar"
      subtitle="Appointments booked in GoHighLevel"
      actions={(
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => setDialog({ mode: 'create' })} data-testid="calendar-new">
            <Plus size={14} /> New appointment
          </Button>
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5" role="group" aria-label="Calendar view" data-testid="calendar-view-toggle">
            {['list', 'week'].map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                onClick={() => showView(v)}
                data-testid={`calendar-view-${v}`}
                className={`px-3 h-8 rounded-md text-sm font-medium ${view === v ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                {v === 'list' ? 'List' : 'Week'}
              </button>
            ))}
          </div>
          {canViewAll && (
            <OwnerFilter value={ownerFilter} onChange={setOwnerFilter} staff={staff} canViewAll testId="calendar-owner-filter" />
          )}
        </div>
      )}
    >
      {view === 'list' && (
        <div className="flex gap-1 mb-4 border-b border-slate-200" role="tablist" data-testid="calendar-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              data-testid={`calendar-tab-${t.key}`}
              className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === t.key ? 'border-brand-accent text-brand-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {view === 'week' ? (
        <WeekGrid
          week={week}
          rows={rows}
          loading={rows === null}
          error={loadError}
          onRetry={() => setReload((n) => n + 1)}
          onWeekChange={showWeek}
          staff={staff}
          savingIds={savingIds}
          onStatusChange={changeStatus}
          onCreate={(start) => setDialog({ mode: 'create', initialStart: start })}
          onReschedule={dragReschedule}
          onRescheduleOpen={(a) => setDialog({ mode: 'reschedule', appointment: a })}
          onEditDetails={setDetailsFor}
        />
      ) : rows === null ? (
        <div className="p-8 flex items-center justify-center text-slate-400" data-testid="calendar-loading">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading appointments...
        </div>
      ) : loadError ? (
        <div role="alert" className="bg-red-50 border border-red-100 rounded-2xl p-12 text-center text-red-700" data-testid="calendar-error">
          <p className="text-sm">Couldn’t load appointments.</p>
          <p className="text-xs mt-1">{loadError}</p>
          <Button size="sm" variant="outline" className="mt-4" onClick={() => setReload((n) => n + 1)} data-testid="calendar-retry">
            Retry
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="calendar-empty">
          <p className="text-sm">No appointments here.</p>
          <p className="text-xs mt-1">Appointments booked in GoHighLevel show up here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto" data-testid="calendar-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Contact</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Time</th>
                <th className="px-4 py-3 font-semibold">Calendar</th>
                <th className="px-4 py-3 font-semibold">Address</th>
                <th className="px-4 py-3 font-semibold">Notes</th>
                <th className="px-4 py-3 font-semibold">Owner</th>
                <th className="px-4 py-3" aria-label="Open in GHL" />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const status = (a.status || '').toLowerCase();
                return (
                  <tr key={a.appointment_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`calendar-row-${a.appointment_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-start">
                        <LongTextCell
                          id={`appt-title-${a.appointment_id}`}
                          text={a.title}
                          lines={2}
                          maxWidth={240}
                          className={`font-medium ${status === 'cancelled' ? 'line-through text-slate-400' : 'text-brand-navy'}`}
                        />
                        {a.source === 'ghl' && <span className="ml-2 align-middle"><GhlBadge testId={`ghl-badge-${a.appointment_id}`} /></span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {a.contact_id ? (
                        <LongTextCell id={`appt-contact-${a.appointment_id}`} text={a.contact_name || 'View contact'} lines={2} maxWidth={200}>
                          <Link to={`/portal/contacts/${a.contact_id}`} className="text-brand-navy underline" data-testid={`calendar-contact-${a.appointment_id}`}>
                            {a.contact_name || 'View contact'}
                          </Link>
                        </LongTextCell>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {a.source === 'ghl' ? (
                        <Select
                          value={status}
                          disabled={savingIds.has(a.appointment_id)}
                          onValueChange={(v) => changeStatus(a, v)}
                        >
                          <SelectTrigger
                            data-testid={`calendar-status-${a.appointment_id}`}
                            className={`h-7 w-auto min-w-[7.5rem] text-[10px] uppercase font-semibold rounded-full border-0 px-2 py-0.5 ${STATUS_STYLE[status] || 'bg-slate-100 text-slate-600'} ${savingIds.has(a.appointment_id) ? 'opacity-60' : ''}`}
                          >
                            <SelectValue placeholder={a.status || '—'} />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : a.status ? (
                        <span className={`text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 ${STATUS_STYLE[status] || 'bg-slate-100 text-slate-600'}`}>{a.status}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{when(a)}</td>
                    <td className="px-4 py-3 text-slate-600">{a.calendar_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-600" data-testid={`calendar-address-${a.appointment_id}`}>
                      {a.address ? <LongTextCell id={`appt-address-${a.appointment_id}`} text={a.address} lines={2} maxWidth={200} /> : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600" data-testid={`calendar-notes-${a.appointment_id}`}>
                      {a.notes ? <LongTextCell id={`appt-notes-${a.appointment_id}`} text={a.notes} lines={2} maxWidth={260} /> : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <OwnerChip userId={a.owner_id} name={a.owner_name} staff={staff} showName />
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap space-x-3">
                      {a.source === 'ghl' && (
                        <button type="button" onClick={() => setDetailsFor(a)} className="text-xs text-brand-navy underline" data-testid={`calendar-edit-details-${a.appointment_id}`}>
                          Edit details
                        </button>
                      )}
                      {a.ghl_edit_url && (
                        <a href={a.ghl_edit_url} target="_blank" rel="noopener noreferrer"
                          className="text-xs inline-flex items-center gap-0.5 text-indigo-700 underline" data-testid={`calendar-open-ghl-${a.appointment_id}`}>
                          Open in GHL <ExternalLink size={11} />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {hasMore && (
            <div className="flex justify-center border-t border-slate-100 p-3">
              <Button size="sm" variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="calendar-load-more">
                {loadingMore && <Loader2 className="animate-spin" size={14} />}
                {tab === 'upcoming' ? 'Load later appointments' : 'Load older appointments'}
              </Button>
            </div>
          )}
        </div>
      )}
      {dialog && (
        <BookDialog
          open
          mode={dialog.mode}
          appointment={dialog.appointment}
          initialStart={dialog.initialStart}
          canViewAll={canViewAll}
          me={me}
          onClose={() => setDialog(null)}
          onSaved={placeRow}
          onReschedule={rescheduleFromDialog}
        />
      )}
      {detailsFor && (
        <DetailsDialog
          open
          appointment={detailsFor}
          onClose={() => setDetailsFor(null)}
          onSaved={swapRow}
        />
      )}
    </PageContainer>
  );
}
